import { Buffer } from 'buffer';
import { ASPLS } from './baseline_aspls';
const dayRegexp = /([A-z]+)\s(\d+)/g;

export function indexOfMax(arr) {
  if (arr.length === 0) {
    return -1;
  }

  var max = arr[0];
  var maxIndex = 0;

  for (var i = 1; i < arr.length; i++) {
    if (arr[i] > max) {
      maxIndex = i;
      max = arr[i];
    }
  }

  return maxIndex;
}

export const normalizeSpectra = (spectra) => {
  let normalizedSpectra = [];
  for (let i = 0; i < spectra.length; i++) {
    normalizedSpectra[i] = spectra[i].map(s => {
      return Math.round((s / Math.sqrt(spectra[i].map(x => Math.pow(x, 2)).reduce((a, b) => a + b, 0))) * 65535);
    });
  }
  return normalizedSpectra;
}

export const removeBadPixels = (rawSpectrum, badPixels) => {
  let spectrum = [...rawSpectrum];
  for (let i = 0; i < badPixels.length; i++) {
    if (badPixels[i] >= spectrum.length) {
      continue;
    }
    if (badPixels[i] === 0) {
      spectrum[0] = spectrum[1]
    } else if (badPixels[i] === spectrum.length - 1) {
      spectrum[spectrum.length - 1] = spectrum[spectrum.length - 2]
    } else {
      spectrum[badPixels[i]] = Math.round((spectrum[badPixels[i] - 1] + spectrum[badPixels[i] + 1]) / 2)
    }
  }

  return spectrum;
}

export const calibrateRamanIntensity = (spectrum, coeffs) => {
  let factors = [...Array(spectrum.length).keys()].map(pixel => {
    return Math.pow(10, coeffs.reduce((prev, curr, i) => prev + (curr * Math.pow(pixel, i))));
  });
  return spectrum.map((p, i) => p * factors[i]);
}

export const addWavenumbers = (wavenumbers, spectra) => {
  let wavenumberCorrected = [];
  for (let i = 0; i < spectra.length; i++) {
    wavenumberCorrected[i] = [];
    for (let j = 0; j < spectra[i].length; j++) {
      wavenumberCorrected[i][j] = [wavenumbers[j], spectra[i][j]];
    }
  }
  return wavenumberCorrected;
}

export const mean = (array) => array.reduce((a, b) => a + b) / array.length;

export const getWeek = () => {
  let week = new Date();
  week.setDate(week.getDate() - week.getDay());
  return (week.toString().match(/([A-z]+)\s(\d+)\s(\d+)/g)[0]);
}

export const getFormattedWeekString = (week) => {
  week = week ? new Date(week) : new Date();
  let firstDay = week.toString().match(dayRegexp)[0];
  week.setDate(week.getDate() + 7);
  let lastDay = week.toString().match(dayRegexp)[0];

  return `${firstDay} - ${lastDay}`
}

export const setClipboard = async (ClipboardItem, map, index, wavenumbers) => {
  const type = 'text/plain';
  const blob = new Blob([`${wavenumbers}\n${map[index].spectrum.processed}`], { type });

  const cbi = new ClipboardItem({
    [type]: blob
  });
  await navigator.clipboard.write([cbi]);
}

export const interpolate = (standardWavenumbers, deviceWavenumbers, intensityValues) => {
  if (standardWavenumbers[0] < deviceWavenumbers[0] || standardWavenumbers.slice(-1)[0] > deviceWavenumbers.slice(-1)[0]) {
    console.error(`Interpolation out of range.`);
    return;
  }
  let interpolatedSpectrum = [];
  let deviceWavenumberIndex = 0
  for (let i = 0; i < standardWavenumbers.length; i++) {
    while (deviceWavenumbers[deviceWavenumberIndex] <= standardWavenumbers[i]) {
      deviceWavenumberIndex++;
    }
    // deviceWavenumberIndex = Math.min(deviceWavenumberIndex, deviceWavenumbers.length - 1);
    // Note: use line above if the device wavenumber ends at exactly 2324 but the current spec for our devices is a minimum of 2500 so this should be fine for now
    interpolatedSpectrum.push(linearInterpolation(standardWavenumbers[i], deviceWavenumbers[deviceWavenumberIndex - 1], intensityValues[deviceWavenumberIndex - 1], deviceWavenumbers[deviceWavenumberIndex], intensityValues[deviceWavenumberIndex]));
  }
  return interpolatedSpectrum;
}

function linearInterpolation(x, x0, y0, x1, y1) {
  return y0 + (y1 - y0) * ((x - x0) / (x1 - x0));
}

export const snvNormalize = (spectrum) => {
  const mean = spectrum.reduce((a, b) => a + b) / spectrum.length;
  const std = Math.sqrt(spectrum.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / (spectrum.length - 1));
  const snvNormalizedSpectrum = spectrum.map(x => (x - mean) / std);
  const minValue = Math.min(...snvNormalizedSpectrum);
  const normalizedSpetrum = snvNormalizedSpectrum.map(value => value - minValue);
  return normalizedSpetrum;
}

export function arrayBufferToBufferCycle(ab) {
  var buffer = new Buffer(ab.byteLength);
  var view = new Uint8Array(ab);
  for (var i = 0; i < buffer.length; ++i) {
    buffer[i] = view[i];
  }
  return buffer;
}

export function isUUID(str) {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(str);
}

export function hasKey(obj) {
  return typeof obj === 'object' && obj !== null && Object.keys(obj).length > 0; // add check to see if first key is not empty string?
}

export const chunkArrayIntoN = (arr, n) => {
  let baseChunkSize = Math.floor(arr.length / n);
  let numChuncksToIncrement = arr.length % n;
  let startIndex = 0;
  let chunkedArray = [];
  for (let i = 0; i < n; i++) {
    chunkedArray[i] = arr.slice(startIndex, startIndex + baseChunkSize + (i < numChuncksToIncrement ? 1 : 0));
    startIndex += baseChunkSize + (i < numChuncksToIncrement ? 1 : 0);
  }
  return chunkedArray;
}

export const sumValuesByKeyMatch = (obj, stringArray) => {
  if (!obj) return null;
  let matchingKeys = Object.keys(obj).filter(key => stringArray.some(str => key === str));
  if (matchingKeys.length === 0) return -1;
  let sum = matchingKeys.reduce((sum, key) => sum + obj[key], 0);
  return sum ? sum : 0;
}

export const calculateR2 = (yActual, yPredicted) => {
  // Check if arrays are valid and have same length
  if (!Array.isArray(yActual) || !Array.isArray(yPredicted) || yActual.length !== yPredicted.length || yActual.length === 0) {
    return null;
  }

  // Calculate mean of actual values
  const meanActual = yActual.reduce((sum, val) => sum + val, 0) / yActual.length;

  // Calculate Total Sum of Squares (SST) and Residual Sum of Squares (SSR)
  let sst = 0; // Total sum of squares
  let ssr = 0; // Residual sum of squares

  for (let i = 0; i < yActual.length; i++) {
    sst += Math.pow(yActual[i] - meanActual, 2);
    ssr += Math.pow(yActual[i] - yPredicted[i], 2);
  }

  // Check if SST is zero (no variance in yActual)
  if (sst === 0) {
    // If SSR is also zero, predictions match actual values perfectly
    if (ssr === 0) {
      return 1; // Perfect fit
    }
    // Otherwise, R² is undefined or not meaningful
    return 0; // Or return null if you prefer to indicate undefined
  }

  // Calculate R²
  const r2 = 1 - (ssr / sst);

  // Handle edge cases
  if (isNaN(r2) || !isFinite(r2)) {
    return 0;
  }

  return Math.max(0, Math.min(1, r2));
}

export const createMasterDarkReference = (darkSpectra) => {
  const nSpectra = darkSpectra.length;
  const nPixels = darkSpectra[0].length;

  // Pre-allocate result array
  const masterDark = new Float64Array(nPixels); // use Float64 for precision

  // Temporary arrays to avoid repeated allocation inside the loop
  const values = new Float64Array(nSpectra);
  const absDev = new Float64Array(nSpectra);

  for (let i = 0; i < nPixels; i++) {
    // 1. Extract column and sort once
    for (let j = 0; j < nSpectra; j++) {
      values[j] = darkSpectra[j][i];
    }
    values.sort((a, b) => a - b);

    // 2. Median
    let median;
    if (nSpectra % 2 === 0) {
      median = (values[nSpectra >> 1] + values[(nSpectra >> 1) - 1]) / 2;
    } else {
      median = values[nSpectra >> 1];
    }

    // 3. MAD → robust sigma (1.4826 factor makes it equivalent to std for Gaussian)
    for (let j = 0; j < nSpectra; j++) {
      absDev[j] = Math.abs(values[j] - median);
    }
    absDev.sort((a, b) => a - b);
    const mad = absDev[nSpectra >> 1];
    const sigma = mad === 0 ? 1 : 1.4826 * mad;

    // 4. Sigma-clipping
    const threshold = 6 * sigma;
    let sum = 0;
    let count = 0;

    for (let j = 0; j < nSpectra; j++) {
      if (Math.abs(values[j] - median) <= threshold) {
        sum += values[j];
        count++;
      }
    }

    // If somehow everything got rejected (extremely rare), fall back to median
    masterDark[i] = count > 0 ? sum / count : median;
  }

  const masterDarkRegularArray = Array.from(masterDark, Math.round);

  return masterDarkRegularArray;
};

export const isGattAvailable = (characteristic) =>
    characteristic?.service?.device?.gatt?.connected;
